import React, { useEffect, useState } from "react";
import {
  FaTrash,
  FaFilter,
  FaTimes,
  FaUserShield,
  FaUserCog,
  FaUser
} from "react-icons/fa";
import { getAdminList, deleteAdmin, updateAdminStatus } from "../../api/endpoints";
import Swal from "../../utils/swalCompat";
import PageHeader from "../../components/PageHeader";

// -----------------------------------------------------------------------------
// MAIN COMPONENT
// -----------------------------------------------------------------------------

const AdminList = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Fetch admin list
  const fetchAdminList = async () => {
    try {
      setLoading(true);

      // Prepare query parameters
      const params = {
        page,
        limit
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      if (roleFilter) {
        params.role = roleFilter;
      }

      console.log("Fetching Admins with params:", params);
      const response = await getAdminList(params);
      console.log("Admin List Response:", response);

      // Handle response structure
      if (response.success) {
        const responseData = response.data;
        setAdmins(responseData.admins || []);
        setTotalPages(responseData.pagination?.totalPages || 1);
        setTotalItems(responseData.pagination?.total || 0);

        if (responseData.pagination?.page) {
          setPage(responseData.pagination.page);
        }
      } else {
        Swal.fire("Error", response.message || "Failed to fetch admin list", "error");
      }
    } catch (error) {
      console.error("Error fetching admin list:", error);
      Swal.fire("Error", error.response?.data?.message || "Failed to fetch admin list", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch on page/filter change
  useEffect(() => {
    fetchAdminList();
  }, [search, statusFilter, roleFilter, page]);

  // Debounce search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (search || statusFilter || roleFilter) {
        setPage(1);
        fetchAdminList();
      }
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [search, statusFilter, roleFilter]);

  // Get role badge
  const getRoleBadge = (role) => {
    const roleMap = {
      'SUPER_ADMIN': { color: 'bg-danger', icon: <FaUserShield /> },
      'ADMIN': { color: 'bg-primary', icon: <FaUserCog /> },
      'SUB_ADMIN': { color: 'bg-info', icon: <FaUser /> },
      'OPERATOR': { color: 'bg-success', icon: <FaUser /> }
    };
    const roleData = roleMap[role] || roleMap['OPERATOR'];
    return (
      <span className={`badge ${roleData.color} d-inline-flex align-items-center gap-1`}>
        {roleData.icon} {role}
      </span>
    );
  };

  // Handle status toggle with API integration
  const handleStatusToggle = async (adminId, currentStatus) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'activate' : 'deactivate';

    const result = await Swal.fire({
      title: `Are you sure?`,
      text: `You want to ${action} this admin?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newStatus ? '#28a745' : '#d33',
      confirmButtonText: `Yes, ${action}`
    });

    if (result.isConfirmed) {
      try {
        setTogglingId(adminId);

        // Call API to update status
        const response = await updateAdminStatus({
          adminId: adminId,
          isActive: newStatus
        });

        console.log("Status update response:", response);

        if (response.success) {
          // Update the local state immediately
          setAdmins(prevAdmins =>
            prevAdmins.map(admin =>
              (admin.id === adminId || admin._id === adminId)
                ? { ...admin, isActive: newStatus }
                : admin
            )
          );

          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: `Admin ${action}d successfully`,
            timer: 1500,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.message || `Failed to ${action} admin`
          });
        }
      } catch (error) {
        console.error("Status update error:", error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || `Failed to ${action} admin`
        });
      } finally {
        setTogglingId(null);
      }
    }
  };

  // Handle delete admin with API integration
  const handleDelete = async (adminId, adminName) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete admin "${adminName}"? This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, Delete'
    });

    if (result.isConfirmed) {
      try {
        setDeletingId(adminId);

        // Call delete API
        const response = await deleteAdmin(adminId);
        console.log("Delete response:", response);

        if (response.success) {
          await Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: response.message || 'Admin deleted successfully'
          });

          // Refresh the list
          await fetchAdminList();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.message || 'Failed to delete admin'
          });
        }
      } catch (error) {
        console.error("Error deleting admin:", error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to delete admin. Please try again.'
        });
      } finally {
        setDeletingId(null);
      }
    }
  };

  // Pagination handlers
  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setPage(pageNumber);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;

    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  // Clear filters
  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setRoleFilter("");
    setPage(1);
  };

  const hasActiveFilters = search || statusFilter || roleFilter;

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="lead-page">
      <PageHeader
        title="Admin Management"
        subtitle="Manage all admin users"
      />

      <div className="card mb-4 card-light">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
          <h5 className="card-title mb-0">Admin List</h5>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            {showFilter && (
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  style={{ width: '200px' }}
                />

                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  style={{ width: '140px' }}
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>

                <select
                  className="form-select"
                  value={roleFilter}
                  onChange={(e) => {
                    setRoleFilter(e.target.value);
                    setPage(1);
                  }}
                  style={{ width: '150px' }}
                >
                  <option value="">All Roles</option>
                  <option value="SUPER_ADMIN">Super Admin</option>
                  <option value="ADMIN">Admin</option>
                  <option value="SUB_ADMIN">Sub Admin</option>
                  <option value="OPERATOR">Operator</option>
                </select>

                {hasActiveFilters && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={clearFilters}
                    title="Clear all filters"
                  >
                    <FaTimes /> Clear
                  </button>
                )}
              </div>
            )}
            <button
              className={`btn ${showFilter ? 'btn-primary' : 'btn-dark'}`}
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaFilter />
              {showFilter ? ' Hide' : ' Filter'}
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* Stats and Info */}
          <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <span className="text-muted">
                Showing {admins.length} of {totalItems} admins
              </span>
              {hasActiveFilters && (
                <span className="badge bg-info ms-2">Filters Applied</span>
              )}
            </div>
            <div>
              <span className="text-muted">
                Page {page} of {totalPages}
              </span>
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="mb-3 d-flex gap-2 flex-wrap">
              {search && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Search: {search}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => { setSearch(""); setPage(1); }}
                    style={{ background: 'none', border: 'none' }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {statusFilter && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Status: {statusFilter === 'true' ? '' : ''}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => { setStatusFilter(""); setPage(1); }}
                    style={{ background: 'none', border: 'none' }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {roleFilter && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Role: {roleFilter}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => { setRoleFilter(""); setPage(1); }}
                    style={{ background: 'none', border: 'none' }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Table */}
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Password</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && admins.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : admins.length > 0 ? (
                  admins.map((admin, index) => (
                    <tr key={admin.id || admin._id}>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                            style={{
                              width: "35px",
                              height: "35px",
                              fontSize: "14px",
                              fontWeight: "bold"
                            }}
                          >
                            {admin.name?.charAt(0) || "A"}
                          </div>
                          <span>{admin.name || "N/A"}</span>
                        </div>
                      </td>
                      <td>
                        <a href={`mailto:${admin.email}`} className="text-decoration-none">
                          {admin.email || "N/A"}
                        </a>
                      </td>
                      <td>{getRoleBadge(admin.role)}</td>
                      <td>{getRoleBadge(admin.simple_pass)}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {/* Toggle Switch */}
                          <label className="toggle-switch" style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}>
                            <input
                              type="checkbox"
                              checked={admin.isActive}
                              onChange={() => handleStatusToggle(admin.id || admin._id, admin.isActive)}
                              disabled={togglingId === (admin.id || admin._id)}
                              style={{ opacity: 0, width: 0, height: 0 }}
                            />
                            <span
                              className="toggle-slider"
                              style={{
                                position: 'absolute',
                                cursor: 'pointer',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: admin.isActive ? '#4CAF50' : '#ccc',
                                transition: '.4s',
                                borderRadius: '34px',
                                opacity: togglingId === (admin.id || admin._id) ? 0.5 : 1
                              }}
                            >
                              <span
                                style={{
                                  position: 'absolute',
                                  content: '""',
                                  height: '16px',
                                  width: '16px',
                                  left: admin.isActive ? '26px' : '4px',
                                  bottom: '4px',
                                  backgroundColor: 'white',
                                  transition: '.4s',
                                  borderRadius: '50%'
                                }}
                              />
                            </span>
                          </label>
                          <span
                            className={`badge ${admin.isActive ? 'bg-success' : 'bg-danger'}`}
                            style={{ minWidth: '60px' }}
                          >
                            {admin.isActive ? '' : ''}
                          </span>
                          {togglingId === (admin.id || admin._id) && (
                            <span className="spinner-border spinner-border-sm text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{formatDate(admin.createdAt)}</td>
                      <td>
                        <div className="d-flex gap-2 justify-content-center">
                          {/* Only Delete Button */}
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(admin.id || admin._id, admin.name)}
                            disabled={deletingId === (admin.id || admin._id)}
                            title="Delete Admin"
                          >
                            {deletingId === (admin.id || admin._id) ? (
                              <span className="spinner-border spinner-border-sm" role="status"></span>
                            ) : (
                              <FaTrash />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      {hasActiveFilters ? 'No admins found matching your filters' : 'No Admins Found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
              <button
                className="btn btn-outline-secondary"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>

              <div className="d-flex gap-1">
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn ${pageNum === page ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => goToPage(pageNum)}
                    disabled={loading}
                    style={{ minWidth: '40px' }}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                className="btn btn-outline-secondary"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages || loading}
              >
                Next
              </button>
            </div>
          )}

          <div className="d-flex justify-content-center mt-2">
            <small className="text-muted">
              Showing {admins.length} of {totalItems} admins | Page {page} of {totalPages}
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminList;